import React, { useMemo, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

import moment from 'moment'
import qs from 'qs'
import { CRUISE_BANK_OFFERS_FILTERS } from 'constants/cruise'
import { SHORT_MONTH_LONG_YEAR, ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'

import useOfferList from 'hooks/Offers/useOfferList'

import { getThemeClassName } from 'lib/theme/themeUtils'

import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import FilterChip from 'LuxKit/Chips/FilterChip'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import Group from 'components/utils/Group'

type FilterOption = {
  label: string
  value: string
}

function CruisePageLastMinuteOffersCarousel() {
  const intl = useIntl()

  const filterOptions = useMemo(() => {
    const now = moment()
    const upcomingMonthLabel = intl.formatMessage({
      id: 'SzxJqceWpn',
      defaultMessage: 'Upcoming month',
      description: 'Filter chip label for the upcoming month in last-minute cruise offers',
    })

    return new Array(5).fill(0).map((_, index) => {
      const month = moment(now).add(index + 1, 'month')
      const label = index === 0 ? upcomingMonthLabel : month.format(SHORT_MONTH_LONG_YEAR)

      return {
        label,
        value: month.format(ISO_DATE_SHORT_FORMAT),
      }
    })
  }, [intl])

  const [selectedMonth, setSelectedMonth] = useState<FilterOption | null>(filterOptions[0])

  const filters: App.OfferListFilters = {
    departureMonths: [selectedMonth?.value ?? ''],
  }

  const offerList = useOfferList({
    ...CRUISE_BANK_OFFERS_FILTERS,
    ...filters,
  })

  const viewAllLabel = intl.formatMessage({
    id: 'jsB7QE2QVn',
    defaultMessage: 'View all',
    description: 'Action button label to view all last-minute cruise offers',
  })

  return <Group gap={32} direction="vertical">
    <LayoutContainer>
      <Group direction="vertical" gap={20}>
        <Group gap={16} direction="horizontal" horizontalAlign="space-between">
          <Heading variant="heading2">
            <FormattedMessage
              id="on3UAkkTDC"
              defaultMessage="<i>Last-minute</i> cruises"
              description="Section heading for last-minute cruise offers in the cruises section"
              values={{ i: (chunks) => <i>{chunks}</i> }}
            />
          </Heading>

          <TextButton
            to={`/search/cruises?${qs.stringify(filters, { arrayFormat: 'repeat' })}`}
            kind="tertiary"
            variant="dark"
          >
            {viewAllLabel}
          </TextButton>
        </Group>
        <MonthTabButtonsGroup>
          <Group direction="horizontal" gap={8}>
            {filterOptions.map((option) => (
              <FilterChip
                key={option.value}
                selected={selectedMonth?.value === option.value}
                size="medium"
                onClick={() => setSelectedMonth(option)}
              >
                {option.label}
              </FilterChip>
            ))}
          </Group>
        </MonthTabButtonsGroup>
      </Group>
    </LayoutContainer>

    <OfferListCarousel
      tileStyle="card-default"
      tileSize="medium"
      overrideOfferList={offerList}
      className={getThemeClassName({ desktop: 'default' })}
    />
  </Group>
}

export default CruisePageLastMinuteOffersCarousel
